import { useCallback, useEffect, useState } from 'react';
import { appointmentService } from '../../../../services/appointment.service';
import { AppointmentOptions, AppointmentResponseDto, AppointmentStatus } from '../../../../types/appointment.types';
import { toastService } from '../../../../services/toast.service';

const PAGE_SIZE_DEFAULT = 25;

function storageKey(expertId?: number) {
  return expertId ? `filters:expert-${expertId}-client-appointments` : 'filters:client-appointments';
}

function readStorageStatus(key: string): string {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw).status ?? '') : '';
  } catch { return ''; }
}

function writeStorageStatus(key: string, value: string) {
  try {
    const raw = localStorage.getItem(key);
    const saved = raw ? JSON.parse(raw) : {};
    localStorage.setItem(key, JSON.stringify({ ...saved, status: value }));
  } catch {}
}

export function useClientAppointments(expertId?: number) {
  const key = storageKey(expertId);

  const [appointments, setAppointments] = useState<AppointmentResponseDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState<AppointmentOptions>({ statuses: [], clients: [], experts: [] });
  const [statusFilter, setStatusFilter] = useState(() => readStorageStatus(storageKey(expertId)));
  const [expertFilter, setExpertFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE_DEFAULT);

  const refreshOptions = useCallback(() => {
    appointmentService.getOptions('client', expertId).then(setOptions).catch(() => {});
  }, [expertId]);

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await appointmentService.getMyAppointments({
        page,
        limit,
        status: statusFilter as AppointmentStatus | '',
        expertId: expertId ?? (expertFilter ? Number(expertFilter) : undefined),
      });
      setAppointments(response.data);
      setTotal((response.meta ?? response.pagination)?.total ?? 0);
    } catch {
      toastService.error('Не вдалося завантажити список записів.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, expertFilter, expertId]);

  useEffect(() => {
    fetchAppointments();
    refreshOptions();
  }, [fetchAppointments, refreshOptions]);

  const handleReset = () => {
    setStatusFilter('');
    setExpertFilter('');
    setPage(1);
  };

  const onStatusChange = (v: string) => {
    setStatusFilter(v);
    setPage(1);
    writeStorageStatus(key, v);
  };

  return {
    appointments,
    total,
    loading,
    options,
    fetchAppointments,
    statusFilter,
    expertFilter,
    onStatusChange,
    onExpertChange: (v: string) => { setExpertFilter(v); setPage(1); },
    page,
    limit,
    onPageChange: setPage,
    onLimitChange: (v: number) => { setLimit(v); setPage(1); },
    handleReset,
  };
}
